import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import Store from 'store/store';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import { BoxWrapper, Button, Modal } from 'components/Base';
import PolicyAnalysis from 'components/ExistingPolicies/PolicyAnalysis';
import Router from 'components/Router/Router';
import { Loader } from 'components/Base';
import withMeta from 'hoc/withMeta';
import withCaseInfo from 'hoc/withCaseInfo';
import api from 'api';
import { Notify } from 'utils';
import BottomNavBar from 'components/Cases/BottomNavBar';
import Icons from 'components/Icons';
import { reduxForm, Form } from 'redux-form';
import ReportOptions from 'utils/ReportOptions';
import selectors from 'store/selectors';
import { useDispatch } from 'react-redux';
import {
  fetchDataIfNeeded,
  getExistingPolicies,
  getRiskProfiles
} from 'components/Cases/utils';
import messages from 'consts/messages';

const { useParams, useHistory } = Router;
const { connect, actions } = Store;

interface PolicyAnalysisPageProps {
  getPastPerfromanceData: any;
  existingPolicies: any;
  updatePerformancePeriod: Function;
  riskSummaries: any;
  setRiskSummaries: any;
  caseInfo: any;
}

const PolicyAnalysisPage = ({
  getPastPerfromanceData,
  existingPolicies,
  updatePerformancePeriod,
  riskSummaries,
  caseInfo
}: PolicyAnalysisPageProps) => {
  const dispatch = useDispatch();
  const isEvalue = caseInfo.risk_profiler === 'EValue';
  const [isLoading, setIsLoading] = useState(true);
  const [isPastPerformanceLoading, setIsPastPerformanceLoading] =
    useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);

  const [sections] = useState([
    {
      items: [
        'Risk analysis',
        isEvalue ? 'Typical asset allocation' : null,
        isEvalue ? 'Forecast investment returns' : null,
        isEvalue ? null : 'About your risk group',
        'Existing portfolio: Overview',
        'Existing portfolio: Analysis'
      ].filter(Boolean)
    },
    {
      title: 'Model portfolio view:',
      items: ['Show underlying funds']
    }
  ]);

  const [reportSections, setReportSections] = useState([]);

  const selectedOptions = Object.values(reportSections)
    .filter((item: any) => item.isSelected)
    .map((item: any) => item.name);

  const { portfolio } = useParams() as any;
  const history = useHistory();

  const printOptions = (type: string) => {
    history.push({
      pathname: `/${portfolio}/ips/print`,
      state: {
        downloadType: type,
        upload_to_io: false,
        portfolioType: 'existing',
        report_sections: selectedOptions
      }
    });
  };

  const getAllData = () => {
    Promise.all([
      dispatch(
        fetchDataIfNeeded('portfolioRiskScore', () =>
          api.getPortfolioRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesRiskScore', () =>
          api.getExistingPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesCharges', () =>
          api.getExsitinPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingAssetAllocations', () =>
          api.getAssetAllocationData({ portfolio }, 'existing')
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingSectorBreakdown', () =>
          api.getSectorBreakdownData({ portfolio }, 'existing')
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingRegionlBreakdown', () =>
          api.getRegionalBreakdownData({ portfolio }, 'existing')
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingCountryBreakdown', () =>
          api.getCountryBreakdownData({ portfolio }, 'existing')
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedRiskAnalysis', () =>
          api.getRiskAnalysisData({ portfolio }, 'existing')
        ) as any
      )
    ]).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(getRiskProfiles(portfolio) as any);
    updatePerformancePeriod();
    // api.evaluate({ portfolio }).catch((err: any) => {
    //   errHelper(err);
    // });
    dispatch(getExistingPolicies(portfolio) as any).then(() => {
      getAllData();
    });

    getPastPerfromanceData({ portfolio })
      .then(() => {
        setIsPastPerformanceLoading(false);
      })
      .catch(() => {
        setIsPastPerformanceLoading(false);
        Notify.error('Error while loading past performance data');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dashboard>
      <Loader isLoading={isLoading}>
        <div style={{ paddingBottom: '86px' }}>
          <PolicyAnalysis
            isPastPerformanceLoading={isPastPerformanceLoading}
            portfolio={portfolio}
          />
        </div>
        {existingPolicies.length > 0 && (
          <BottomNavBar
            rightButtons={[
              <>
                <Button
                  style={{ marginRight: '24px' }}
                  onClick={() => {
                    if (
                      riskSummaries.length === 0 ||
                      !riskSummaries[0].time_horizon ||
                      !riskSummaries[0].recommended_risk_profile
                    ) {
                      Notify.warn(
                        'Report unavailable',
                        caseInfo.risk_profiler === 'EValue'
                          ? messages.INSUFFICIENT_RISK_PROFILE_DATA_EVALUE
                          : messages.INSUFFICIENT_RISK_PROFILE_DATA_MORNINGSTAR
                      );
                      return;
                    }
                    setIsModelOpen(true);
                  }}
                >
                  Download report
                </Button>

                <Button
                  endIcon={<Icons.RightArrow />}
                  key="Build recommendations button"
                  onClick={() => history.push(`/${portfolio}/recommendations`)}
                >
                  Build recommendations
                </Button>
              </>
            ]}
          />
        )}
      </Loader>
      <Modal isOpen={isModelOpen} closeModalFunc={() => setIsModelOpen(false)}>
        <BoxWrapper
          title="Portfolio Review report options"
          closeModal={() => setIsModelOpen(false)}
        >
          <div className="printModal">
            <Form>
              <ReportOptions
                sections={sections}
                setSelectedSections={setReportSections}
                existingPolicies={existingPolicies}
              />
            </Form>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                disabled={!selectedOptions.length}
                onClick={() => {
                  printOptions('pdf');
                }}
              >
                Download as PDF
              </Button>
              <Button
                disabled={!selectedOptions.length}
                key="Download as Word button"
                onClick={() => {
                  printOptions('word');
                }}
              >
                Download as Word
              </Button>
            </div>
          </div>
        </BoxWrapper>
      </Modal>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskSummaries: selectors.caseSelectors.getRiskSummary(state),
      existingPolicies: selectors.caseSelectors.existingPolicies(state),
      caseInfo: selectors.caseSelectors.case(state)
    }),
    {
      getPastPerfromanceData:
        actions.existingPolicyAnalysisActions.getPastPerfromanceData,
      updatePerformancePeriod:
        actions.investmentAnalyserActions.updatePerformancePeriod,
      setRiskSummaries: actions.caseActions.setRiskSummaries
    }
  ),
  reduxForm({
    form: 'ipsPrint',
    enableReinitialize: true
  }),
  withMeta,
  withCaseInfo
)(PolicyAnalysisPage);
